import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, UploadSimple, ListBullets } from "@phosphor-icons/react";
import { Heading } from "./NKDaily";

const resolveUrl = (u) => (u && u.startsWith("/api/uploads/")) ? `${BACKEND_URL}${u}` : u;

export default function Sponsors() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [slate, setSlate] = useState(null); // sponsor object whose slate is being managed

  const load = async () => {
    try { const { data } = await api.get("/admin/sponsors"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (s) => {
    try {
      if (s.sponsor_id) await api.put(`/admin/sponsors/${s.sponsor_id}`, s);
      else await api.post("/admin/sponsors", s);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (s) => {
    if (!confirm(`Delete sponsor "${s.name}"?`)) return;
    try { await api.delete(`/admin/sponsors/${s.sponsor_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <Heading title="Sponsors" subtitle="Future goldmine. Branded events, sponsored questions, prize-pool partners." />
        <button onClick={() => setEditing({ name: "", description: "", start_date: new Date().toISOString(), country: "ALL", status: "draft", budget_currency: "USD", budget_amount: 0 })}
          className="px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm inline-flex items-center gap-2 shrink-0">
          <Plus size={14} weight="bold" /> New sponsor
        </button>
      </header>

      {rows.length === 0 && (
        <div className="nk-card p-10 text-center space-y-2">
          <p className="font-display text-xl">No sponsors yet.</p>
          <p className="text-sm text-nk-mute">When you onboard a brand like MTN or Glo, create a campaign here with logo, banner, and budget.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((s) => (
          <div key={s.sponsor_id} className="nk-card p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="font-display text-lg">{s.name}</p>
              <div className="flex gap-1">
                <button onClick={() => setEditing(s)} className="p-1.5 text-nk-mute hover:text-nk-text"><PencilSimple size={14} /></button>
                <button onClick={() => del(s)} className="p-1.5 text-nk-mute hover:text-nk-bad"><Trash size={14} /></button>
              </div>
            </div>
            <p className="text-xs text-nk-mute mb-3">{s.description}</p>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <KV k="Status" v={<span className="capitalize">{s.status}</span>} />
              <KV k="Budget" v={<span className="font-mono">{s.budget_currency} {(s.budget_amount || 0).toLocaleString()}</span>} />
              <KV k="Reach" v={<span className="font-mono">{(s.views || 0).toLocaleString()} views</span>} />
            </div>
            <button
              onClick={() => setSlate(s)}
              data-testid={`sponsor-slate-btn-${s.sponsor_id}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-nk-line hover:border-nk-accent rounded-sm text-xs text-nk-mute hover:text-nk-text transition-colors"
            >
              <ListBullets size={12} weight="bold" />
              Manage Saturday question slate
            </button>
          </div>
        ))}
      </div>

      {editing && <SponsorEditor initial={editing} onClose={() => setEditing(null)} onSave={save} />}
      {slate && <SlateManager sponsor={slate} onClose={() => setSlate(null)} />}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <p className="text-[10px] text-nk-dim uppercase tracking-wider mb-0.5">{k}</p>
      <div>{v}</div>
    </div>
  );
}

function SponsorEditor({ initial, onClose, onSave }) {
  const [s, setS] = useState(initial);
  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-medium">{initial?.sponsor_id ? "Edit sponsor" : "New sponsor"}</h2>
          <button onClick={onClose} className="text-nk-mute hover:text-nk-text"><X size={18} /></button>
        </div>
        <div className="space-y-3 text-sm">
          <F label="Name"><Input v={s.name} onC={(v) => set("name", v)} placeholder="e.g. MTN Saturday Challenge" /></F>
          <F label="Description"><Textarea v={s.description} onC={(v) => set("description", v)} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Country"><Select v={s.country} onC={(v) => set("country", v)} opts={[["ALL", "All"], ["NG", "Nigeria"], ["GH", "Ghana"]]} /></F>
            <F label="Status"><Select v={s.status} onC={(v) => set("status", v)} opts={[["draft", "Draft"], ["live", "Live"], ["paused", "Paused"], ["ended", "Ended"]]} /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Budget currency"><Select v={s.budget_currency} onC={(v) => set("budget_currency", v)} opts={[["USD", "USD"], ["NGN", "NGN"], ["GHS", "GHS"]]} /></F>
            <F label="Budget amount"><Input type="number" v={s.budget_amount} onC={(v) => set("budget_amount", Number(v))} /></F>
          </div>
          <F label="Logo"><FileUpload current={s.logo_url} onUploaded={(url) => set("logo_url", url)} /></F>
          <F label="Banner"><FileUpload current={s.banner_url} onUploaded={(url) => set("banner_url", url)} /></F>

          <div className="flex gap-2 pt-3">
            <button onClick={() => onSave(s)} className="flex-1 px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm hover:bg-nk-accentSoft">
              {initial?.sponsor_id ? "Save" : "Create"}
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-nk-line text-sm text-nk-mute rounded-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><p className="text-[11px] text-nk-mute uppercase tracking-wider mb-1">{label}</p>{children}</div>;
}

function FileUpload({ current, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      {current && (
        <img src={resolveUrl(current)} alt="preview"
             className="w-12 h-12 object-contain border border-nk-line rounded-sm bg-nk-surface" />
      )}
      <label className={`px-3 py-2 border border-nk-line rounded-sm text-xs cursor-pointer hover:border-nk-accent inline-flex items-center gap-2 ${busy ? "opacity-50" : ""}`}>
        <UploadSimple size={12} weight="bold" />
        {busy ? "Uploading…" : current ? "Replace" : "Upload image"}
        <input type="file" accept="image/*" className="hidden" disabled={busy}
               onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {current && (
        <button onClick={() => onUploaded(null)} className="text-xs text-nk-mute hover:text-nk-bad">Remove</button>
      )}
    </div>
  );
}
function Input({ v, onC, type = "text", placeholder }) {
  return <input type={type} value={v} onChange={(e) => onC(e.target.value)} placeholder={placeholder}
    className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-accent outline-none" />;
}
function Textarea({ v, onC }) {
  return <textarea value={v || ""} onChange={(e) => onC(e.target.value)} rows={2}
    className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-accent outline-none resize-none" />;
}
function Select({ v, onC, opts }) {
  return (
    <select value={v} onChange={(e) => onC(e.target.value)} className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm">
      {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  );
}

// === Sponsored Question Slate manager ===========================
function SlateManager({ sponsor, onClose }) {
  const [questions, setQuestions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/admin/sponsors/${sponsor.sponsor_id}/questions`);
      setQuestions(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sponsor.sponsor_id]);

  const addOne = async (q) => {
    try {
      await api.post(`/admin/sponsors/${sponsor.sponsor_id}/questions`, q);
      toast.success("Added");
      setCreating(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addBulk = async (jsonText) => {
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch { toast.error("Invalid JSON"); return; }
    if (!Array.isArray(parsed)) { toast.error("Expected a JSON array"); return; }
    try {
      const { data } = await api.post(`/admin/sponsors/${sponsor.sponsor_id}/questions/bulk`,
        { questions: parsed });
      toast.success(`Inserted ${data.inserted}`);
      setBulkOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (q) => {
    if (!confirm("Delete this question?")) return;
    try {
      await api.delete(`/admin/sponsors/${sponsor.sponsor_id}/questions/${q.qid}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="eyebrow">{sponsor.name}</p>
            <h2 className="font-display text-2xl font-medium">Saturday question slate</h2>
          </div>
          <button onClick={onClose} className="text-nk-mute hover:text-nk-text"><X size={18} /></button>
        </div>
        <p className="text-xs text-nk-mute mb-5">
          Saturday Challenge needs at least <span className="font-mono text-nk-text">30 questions</span> from this sponsor's slate to use them. Otherwise it falls back to the general bank. Current: <span className="font-mono text-nk-text">{questions.length}</span>.
        </p>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setCreating(true)} className="px-3 py-2 bg-nk-accent text-nk-bg text-xs font-medium rounded-sm inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" /> Add question
          </button>
          <button onClick={() => setBulkOpen(true)} className="px-3 py-2 border border-nk-line text-xs text-nk-mute hover:text-nk-text rounded-sm">
            Bulk upload JSON
          </button>
        </div>

        {creating && <QuestionForm onCancel={() => setCreating(false)} onSave={addOne} />}
        {bulkOpen && <BulkForm onCancel={() => setBulkOpen(false)} onSave={addBulk} />}

        <div className="space-y-2 mt-4">
          {questions.length === 0 && <p className="text-sm text-nk-dim">No sponsored questions yet.</p>}
          {questions.map((q) => (
            <div key={q.qid} className="nk-card p-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium">{q.body}</p>
                <button onClick={() => del(q)} className="text-nk-mute hover:text-nk-bad shrink-0"><Trash size={14} /></button>
              </div>
              <ul className="mt-2 grid grid-cols-2 gap-1 text-xs">
                {q.options.map((o, i) => (
                  <li key={i} className={i === q.answer_index ? "text-nk-accent" : "text-nk-mute"}>
                    {String.fromCharCode(65 + i)}. {o} {i === q.answer_index ? "✓" : ""}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-nk-dim mt-2 uppercase tracking-wider">{q.category} · {q.difficulty}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuestionForm({ onCancel, onSave }) {
  const [body, setBody] = useState("");
  const [opts, setOpts] = useState(["", "", "", ""]);
  const [ai, setAi] = useState(0);
  const [cat, setCat] = useState("Sponsored");
  const [diff, setDiff] = useState("medium");
  const submit = () => {
    if (!body.trim() || opts.some((o) => !o.trim())) {
      toast.error("Fill all fields"); return;
    }
    onSave({ body, options: opts, answer_index: ai, category: cat, difficulty: diff });
  };
  return (
    <div className="nk-card p-4 space-y-2 mb-3">
      <Textarea v={body} onC={setBody} />
      {opts.map((o, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="radio" checked={ai === i} onChange={() => setAi(i)} className="accent-nk-accent" />
          <Input v={o} onC={(v) => setOpts(opts.map((x, j) => j === i ? v : x))}
                 placeholder={`Option ${String.fromCharCode(65 + i)}`} />
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2">
        <Input v={cat} onC={setCat} placeholder="Category" />
        <Select v={diff} onC={setDiff} opts={[["easy", "Easy"], ["medium", "Medium"], ["hard", "Hard"]]} />
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={submit} className="flex-1 px-3 py-2 bg-nk-accent text-nk-bg text-xs font-medium rounded-sm">Save question</button>
        <button onClick={onCancel} className="px-3 py-2 border border-nk-line text-xs text-nk-mute rounded-sm">Cancel</button>
      </div>
    </div>
  );
}

function BulkForm({ onCancel, onSave }) {
  const [text, setText] = useState(`[
  {"body":"Sample question?","options":["A","B","C","D"],"answer_index":0,"category":"Sponsored","difficulty":"medium"}
]`);
  return (
    <div className="nk-card p-4 space-y-2 mb-3">
      <p className="text-[11px] text-nk-mute uppercase tracking-wider">Bulk JSON · array of {`{body, options[4], answer_index, category, difficulty}`}</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
                className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-xs font-mono rounded-sm focus:border-nk-accent outline-none" />
      <div className="flex gap-2">
        <button onClick={() => onSave(text)} className="flex-1 px-3 py-2 bg-nk-accent text-nk-bg text-xs font-medium rounded-sm">Upload</button>
        <button onClick={onCancel} className="px-3 py-2 border border-nk-line text-xs text-nk-mute rounded-sm">Cancel</button>
      </div>
    </div>
  );
}
